// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for getting tracking data about an asset event group.
 */
import * as z from 'zod';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { trackingDataSchema } from '@/tracking/api/types';

const eventGroupTrackingResponseSchema = z.object({
  data: trackingDataSchema,
  metadata: responseMetadataSchema,
});

type EventGroupTrackingResponse = z.infer<
  typeof eventGroupTrackingResponseSchema
>;

/**
 * Retrieves tracking data for a given asset event group
 *
 * @param assetId - Unique identifier of the asset to fetch event groups for
 * @param groupId - Unique identifier for the asset event group that the tracing is being pulled for.
 * @returns Promise resolving to the sorted list of event groups and request metadata
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export async function getEventGroupTrackingResponse(
  assetId: string,
  groupId: string
): Promise<EventGroupTrackingResponse> {
  const url = `/v1/asset/${assetId}/event-group/${groupId}/shipping`;
  const response = await api.get(url);
  return parseResponse(eventGroupTrackingResponseSchema, response.data, {
    schemaName: 'EventGroupTracking',
    endpoint: url,
  });
}
